import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Link,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Popover,
  List,
  ListItem,
  CircularProgress,
} from "@mui/material";
import Spark_Logo from "../../assets/baseVersion/Spark_Newlogo.svg";
import { useFormik } from "formik";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import "./reset_Password.css";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import { toast } from "react-toastify";
import { loginValidationSchema } from "./validation_schema";
import { END_POINTS } from "../../shared";
import { Skeleton, } from "@mui/material";

import DoneIcon from "@mui/icons-material/Done";
import { useEncryptService, useTokens } from "../../shared/hooks";
import axiosInstance from "../../axios/axios";
import {
  PrivacyPolicy,
  TermsConditions,
} from "../../modules/authentication/Register";
import { useLocation, useParams } from "react-router-dom";
import ClearIcon from '@mui/icons-material/Clear';
interface ResetPasswordProps {
  initialAuthData?: {
    first_name: string;
    last_name: string;
    email: string;
    valid_token_time: boolean;
    password_expired?: boolean;
  };
}

const ResetPassword: React.FC<ResetPasswordProps> = ({ initialAuthData }) => {


  const { clearTokens } = useTokens();
  const [showPassword, setShowPassword] = useState(false);
  const [confirmpassword, setConfirmpassword] = useState(false);
  const [isPrivacyPolicyModalOpen, setIsPrivacyPolicyModalOpen] =
    useState(false);
  const [isTermsAndConditionsModalOpen, setIsTermsAndConditionsModalOpen] =
    useState(false);
  const [isRegisterSuccess, setIsRegisterSuccess] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [authToken, setAuthToken] = useState<{
    first_name: string;
    last_name: string;
    email: string;
    valid_token_time: boolean;
    password_expired?: boolean;
  } | null>(null);

  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [apiMessage, setApiMessage] = useState<string>("");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { doEncryptLogin } = useEncryptService();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const id = open ? "password-popover" : undefined;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const conditions = [
    { label: "1 Uppercase Letter", regex: /[A-Z]/ },
    { label: "1 Lowercase Letter", regex: /[a-z]/ },
    { label: "1 Number", regex: /[0-9]/ },
    {
      label: "1 Special Character",
      regex: /[@$!%*?&#^_\-+=(){}\[\].,:~`|\\;"'<>/]/,
    }, { label: "Minimum 12 Characters required", regex: /^.{12,}$/ },
    {
      label: "Maximum 20 Characters allowed",
      validate: (password: string) => password.length <= 20,
    },
  ];

  const handleNameInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    if (value.length <= 32) {
      formik.handleChange(e);
    }
  };

  const getFieldError = (fieldName: string, value: string) => {
    if (value.length > 32) {
      return "Maximum 32 characters allowed";
    }
    return formik.errors[fieldName as keyof typeof formik.errors];
  };

  const { id: resetId, token } = useParams<{ id: string; token: string }>();
  // Set when sign-in was refused because the password expired and sent the user here. The link
  // itself says so too (token-validation), which survives a reload or a new tab.
  const expiredFromSignIn = Boolean((useLocation().state as { passwordExpired?: boolean } | null)?.passwordExpired);
  const passwordExpired = expiredFromSignIn || Boolean(authToken?.password_expired);
  const hasNames = Boolean(authToken?.first_name && authToken?.last_name);
  // An expired password always belongs to an existing account, even one whose names were never
  // stored, so it gets the change-password form (email shown) rather than the new-user one.
  const isExistingUser = hasNames || passwordExpired;

  // Add debug logging
  console.log("URL Parameters:", { resetId, token });
  useEffect(() => {
    if (initialAuthData) {
      setAuthToken(initialAuthData);
      setLoading(false);
      return;
    }
    // First validate the token
    const validationResponse = async () => {
      const response = await axiosInstance.get(
        `${END_POINTS.TOKEN_VALIDATION}/${resetId}/${token}`
      );
      if (response?.data?.data) {
        setLoading(false);
        setAuthToken(response?.data?.data);
        if (response?.data?.message) {
          setApiMessage(response.data.message);
        }
      }
      console.log(response, "response");
    };
    validationResponse();
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstname: authToken?.first_name || "",
      lastname: authToken?.last_name || "",
      email: authToken?.email || "",
      password: "",
      confirmpassword: "",
    },
    validationSchema: loginValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (isExistingUser) {
        try {
          setIsLoading(true);
          const response = await axiosInstance.post(
            `${END_POINTS.CREATE_PASSWORD}/${resetId}/${token}`,
            {
              firstname: values.firstname,
              lastname: values.lastname,
              password: doEncryptLogin(values.password),
              password1: doEncryptLogin(values.confirmpassword),
              flag: !hasNames,
            }
          );

          toast(response.data.message, { type: "success" });
          setIsRegisterSuccess(true);
        } catch (error: any) {
          toast.error(error.response?.data?.message || "Something went wrong", {
            type: "error",
          });
        } finally {
          setSubmitting(false);
          setIsLoading(false);
        }
      } else {
        try {
          setIsLoading(true);
          clearTokens();
          const response = await axiosInstance.post(
            `${END_POINTS.CREATE_PASSWORD}/${resetId}/${token}`,
            {
              firstname: values.firstname,
              lastname: values.lastname,
              password: doEncryptLogin(values.password),
              password1: doEncryptLogin(values.confirmpassword),
              flag: true,
            }
          );

          toast(response.data.message, { type: "success" });
          setIsRegisterSuccess(true);
        } catch (error: any) {
          toast.error(error.response?.data?.message || "Something went wrong", {
            type: "error",
          });
        } finally {
          setSubmitting(false);
          setIsLoading(false);
        }
      }
    },
  });

  if (loading) {
    return (
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
        {Array.from({ length: 16 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="text"
            width="100%"
            height={20}
            sx={{ width: '100%' }}
          />
        ))}
      </div>
    );
  }



  return authToken && authToken.valid_token_time ? (isExistingUser ? (
    <>
      {isRegisterSuccess ? (

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "50vh",
          }}
        >
          <DoneIcon sx={{ color: "green", height: "100px", width: "100px" }} />
          <Typography sx={{ mb: 2 }}>
            <strong>New Password has been created!</strong>
          </Typography>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            className={`btn btn-primary`}
            href="/login"
          >
            Sign In
          </Button>
        </div>
      ) : (
        <div className="login-container">
          <div className="login_logo">
            <img src={Spark_Logo} alt="Login Logo" />
          </div>
          <h5 className="mb_2 font_bold mt_0" style={{ fontSize: 18, marginBottom: 8 }}>
            {passwordExpired ? "Your password has expired" : "Create Password."}{" "}
          </h5>
          <p className="login-subtitle" style={{ marginBottom: 16 }}>
            {passwordExpired ? "Choose a new password to continue." : "Enter your new password and confirm "}
          </p>
          <form
            onSubmit={formik.handleSubmit}
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div>
              <TextField
                id="firstname"
                label="First Name"
                variant="outlined"
                required
                fullWidth
                name="firstname"
                disabled={Boolean(authToken.first_name)}
                sx={{ mb: 1.5 }}
                onChange={(e) => handleNameInput(e)}
                onBlur={formik.handleBlur}
                value={formik.values.firstname}
                error={formik.touched.firstname && Boolean(formik.errors.firstname)}
                helperText={formik.touched.firstname && getFieldError("firstname", formik.values.firstname)}
                inputProps={{
                  maxLength: 32,
                }}
              />

              <TextField
                id="lastname"
                label="Last Name"
                variant="outlined"
                required
                fullWidth
                name="lastname"
                disabled={Boolean(authToken.last_name)}
                sx={{ mb: 1.5 }}
                onChange={(e) => handleNameInput(e)}
                onBlur={formik.handleBlur}
                value={formik.values.lastname}
                error={formik.touched.lastname && Boolean(formik.errors.lastname)}
                helperText={formik.touched.lastname && getFieldError("lastname", formik.values.lastname)}
                inputProps={{
                  maxLength: 32,
                }}
              />

              <TextField
                id="email"
                label="Email"
                variant="outlined"
                required
                fullWidth
                name="email"
                disabled
                sx={{ mb: 1.5 }}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                inputProps={{
                  maxLength: 32,
                }}
              />

              <TextField
                id="password"
                label="Password"
                variant="outlined"
                fullWidth
                required
                type={showPassword ? "text" : "password"}
                name="password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                sx={{ mb: 1.5 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span
                        className="visibility-icon"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </span>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                id="confirmpassword"
                label="Confirm Password"
                variant="outlined"
                fullWidth
                required
                type={confirmpassword ? "text" : "password"}
                name="confirmpassword"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.confirmpassword}
                error={
                  formik.touched.confirmpassword && Boolean(formik.errors.confirmpassword)
                }
                helperText={
                  formik.touched.confirmpassword && formik.errors.confirmpassword
                }
                sx={{ mb: 1.5 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span
                        className="visibility-icon"
                        style={{ cursor: "pointer" }}
                        onClick={() => setConfirmpassword(!confirmpassword)}
                      >
                        {confirmpassword ? <Visibility /> : <VisibilityOff />}
                      </span>
                    </InputAdornment>
                  ),
                }}
              />

            </div>

            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                <IconButton
                  onClick={handleClick}
                  aria-describedby={id}
                  sx={{ "&:focus": { outline: "none" }, padding: "4px" }}
                >
                  <InfoIcon style={{ color: "#868fa5", fontSize: 18 }} />
                </IconButton>
                <Popover
                  id={id}
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                >
                  <Typography sx={{ p: 2, maxWidth: 350 }}>
                    <List dense>
                      {conditions.map((condition, index) => {
                        const isMatched = condition.regex
                          ? condition.regex.test(formik.values.password)
                          : condition.validate
                            ? condition.validate(formik.values.password)
                            : false;
                        return (
                          <ListItem key={index} sx={{ display: "flex", gap: 1 }}>
                            {isMatched ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                              <CheckCircleOutlineIcon
                                color="inherit"
                                fontSize="small"
                              />
                            )}
                            {condition.label}
                          </ListItem>
                        );
                      })}
                    </List>
                  </Typography>
                </Popover>

                <Typography
                  style={{
                    fontSize: "13px",
                    fontWeight: 350,
                    lineHeight: "16px",
                    opacity: 0.5,
                  }}
                >
                  Follow the criteria while setting the password
                </Typography>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <Checkbox
                  size="small"
                  checked={isCheckboxChecked}
                  onChange={(e) => setIsCheckboxChecked(e.target.checked)}
                  sx={{ padding: "4px 8px" }}
                />
                <Typography
                  style={{
                    fontSize: "13px",
                    fontWeight: 450,
                    lineHeight: "18px",
                    color: "rgba(0, 0, 0, 0.5)",
                  }}
                >
                  I Accept to the{" "}
                  <Link
                    style={{ color: "#3a2ada", textDecoration: "none", cursor: "pointer" }}
                    onClick={() => setIsPrivacyPolicyModalOpen(true)}
                  >
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    style={{ color: "#3a2ada", textDecoration: "none", cursor: "pointer" }}
                    onClick={() => setIsTermsAndConditionsModalOpen(true)}
                  >
                    Terms & Conditions
                  </Link>
                </Typography>
              </div>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                disabled={!formik.isValid || !formik.dirty || !isCheckboxChecked || isLoading}
                className={`btn btn-primary`}
                type="submit"
              >
                Create Password

                {isLoading && (
                  <CircularProgress size={15} color="inherit" />
                )}          </Button>
            </div>
          </form>
          {isPrivacyPolicyModalOpen && (
            <PrivacyPolicy
              isPrivacyPolicyModalOpen={isPrivacyPolicyModalOpen}
              setIsPrivacyPolicyModalOpen={setIsPrivacyPolicyModalOpen}
            />
          )}
          {isTermsAndConditionsModalOpen && (
            <TermsConditions
              isTermsAndConditionsModalOpen={isTermsAndConditionsModalOpen}
              setIsTermsAndConditionsModalOpen={
                setIsTermsAndConditionsModalOpen
              }
            />
          )}
        </div>
      )}
    </>
  ) : (
    <>
      {isRegisterSuccess ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "50vh",
          }}
        >
          <DoneIcon sx={{ color: "green", height: "100px", width: "100px" }} />
          <Typography sx={{ mb: 2 }}>
            <strong>New Password has been created!</strong>
          </Typography>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            className={`btn btn-primary`}
            href="/login"
          >
            Sign In
          </Button>
        </div>
      ) : (
        <div className="login-container">
          <div className="login_logo">
            <img src={Spark_Logo} alt="Login Logo" />
          </div>
          <h5 className="mb_2 font_bold mt_0" style={{ fontSize: 18, marginBottom: 8 }}>
            {passwordExpired ? "Your password has expired" : "Create Password."}{" "}
          </h5>
          <p className="login-subtitle" style={{ marginBottom: 16 }}>
            {passwordExpired ? "Choose a new password to continue." : "Enter your new password and confirm "}
          </p>
          <form
            onSubmit={formik.handleSubmit}
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div>
              <TextField
                id="firstname"
                label="First Name"
                variant="outlined"
                required
                fullWidth
                name="firstname"
                sx={{ mb: 1.5 }}
                onChange={(e) => handleNameInput(e)}
                onBlur={formik.handleBlur}
                value={formik.values.firstname}
                error={
                  (formik.touched.firstname &&
                    Boolean(formik.errors.firstname)) ||
                  formik.values.firstname.length > 32
                }
                helperText={
                  (formik.touched.firstname &&
                    getFieldError("firstname", formik.values.firstname)) ||
                  (formik.values.firstname.length > 32 &&
                    "Maximum 32 characters allowed")
                }
                inputProps={{
                  maxLength: 32,
                }}
              />

              <TextField
                id="lastname"
                label="Last Name"
                variant="outlined"
                required
                fullWidth
                name="lastname"
                sx={{ mb: 1.5 }}
                onChange={(e) => handleNameInput(e)}
                onBlur={formik.handleBlur}
                value={formik.values.lastname}
                error={
                  (formik.touched.lastname && Boolean(formik.errors.lastname)) ||
                  formik.values.lastname.length > 32
                }
                helperText={
                  (formik.touched.lastname &&
                    getFieldError("lastname", formik.values.lastname)) ||
                  (formik.values.lastname.length > 32 &&
                    "Maximum 32 characters allowed")
                }
                inputProps={{
                  maxLength: 32,
                }}
              />

              <TextField
                id="password"
                label="Password"
                variant="outlined"
                fullWidth
                required
                type={showPassword ? "text" : "password"}
                name="password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                sx={{ mb: 1.5 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span
                        className="visibility-icon"
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </span>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                id="confirmpassword"
                label="Confirm Password"
                variant="outlined"
                fullWidth
                required
                type={confirmpassword ? "text" : "password"}
                name="confirmpassword"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.confirmpassword}
                error={
                  formik.touched.confirmpassword && Boolean(formik.errors.confirmpassword)
                }
                helperText={
                  formik.touched.confirmpassword && formik.errors.confirmpassword
                }
                sx={{ mb: 1.5 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span
                        className="visibility-icon"
                        style={{ cursor: "pointer" }}
                        onClick={() => setConfirmpassword(!confirmpassword)}
                      >
                        {confirmpassword ? <Visibility /> : <VisibilityOff />}
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                <IconButton
                  onClick={handleClick}
                  aria-describedby={id}
                  sx={{ "&:focus": { outline: "none" }, padding: "4px" }}
                >
                  <InfoIcon style={{ color: "#868fa5", fontSize: 18 }} />
                </IconButton>
                <Popover
                  id={id}
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                >
                  <Typography sx={{ p: 2, maxWidth: 350 }}>
                    <List dense>
                      {conditions.map((condition, index) => {
                        const isMatched = condition.regex
                          ? condition.regex.test(formik.values.password)
                          : condition.validate
                            ? condition.validate(formik.values.password)
                            : false;
                        return (
                          <ListItem key={index} sx={{ display: "flex", gap: 1 }}>
                            {isMatched ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                              <CheckCircleOutlineIcon
                                color="inherit"
                                fontSize="small"
                              />
                            )}
                            {condition.label}
                          </ListItem>
                        );
                      })}
                    </List>
                  </Typography>
                </Popover>

                <Typography
                  style={{
                    fontSize: "13px",
                    fontWeight: 350,
                    lineHeight: "16px",
                    opacity: 0.5,
                  }}
                >
                  Follow the criteria while setting the password
                </Typography>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <Checkbox
                  size="small"
                  checked={isCheckboxChecked}
                  onChange={(e) => setIsCheckboxChecked(e.target.checked)}
                  sx={{ padding: "4px 8px" }}
                />
                <Typography
                  style={{
                    fontSize: "13px",
                    fontWeight: 450,
                    lineHeight: "18px",
                    color: "rgba(0, 0, 0, 0.5)",
                  }}
                >
                  I Accept to the{" "}
                  <Link
                    style={{ color: "#3a2ada", textDecoration: "none", cursor: "pointer" }}
                    onClick={() => setIsPrivacyPolicyModalOpen(true)}
                  >
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    style={{ color: "#3a2ada", textDecoration: "none", cursor: "pointer" }}
                    onClick={() => setIsTermsAndConditionsModalOpen(true)}
                  >
                    Terms & Conditions
                  </Link>
                </Typography>
              </div>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                disabled={!formik.isValid || !formik.dirty || !isCheckboxChecked || isLoading}
                className="btn btn-primary"
                type="submit"
              >

                Create Password
                {isLoading && (
                  <CircularProgress
                    size={20}
                    sx={{ color: "white", marginLeft: "2px" }}
                  />
                )}
              </Button>
            </div>
          </form>
          {isPrivacyPolicyModalOpen && (
            <PrivacyPolicy
              isPrivacyPolicyModalOpen={isPrivacyPolicyModalOpen}
              setIsPrivacyPolicyModalOpen={setIsPrivacyPolicyModalOpen}
            />
          )}
          {isTermsAndConditionsModalOpen && (
            <TermsConditions
              isTermsAndConditionsModalOpen={isTermsAndConditionsModalOpen}
              setIsTermsAndConditionsModalOpen={
                setIsTermsAndConditionsModalOpen
              }
            />
          )}
        </div>
      )}
    </>
  )) : (<div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "50vh",
    }}
  >
    <ClearIcon
      sx={{ color: "red", height: "100px", width: "100px" }}
    />

    <Typography sx={{ mb: 1, textAlign: "center" }}>
      <strong>{apiMessage || "Your account Verification link has expired!"}</strong>
    </Typography>

    <Typography sx={{ mb: 2, textAlign: "center" }}>
      {authToken?.email || "Email not available"}
    </Typography>
  </div>)
}


  ;

export default ResetPassword;
